import React from 'react';
import { renderMarkdown } from '../../utils/markdown';
import { parseToGaiaID } from '../../utils/gaiaAddress';
import {
  MessageActionMenu,
  MessageReactionStrip,
  PinnedMessagesStrip,
  ReplyComposerPreview,
  ReplyContext,
  messagePreview
} from './MessageActions';

const GROUP_EMOJIS = [
  '\u{1F600}', '\u{1F604}', '\u{1F602}', '\u{1F60A}', '\u{1F60D}', '\u{1F60E}',
  '\u{1F91D}', '\u{1F64F}', '\u{1F44D}', '\u{1F525}', '\u{2728}', '\u{1F680}',
  '\u{1F512}', '\u{1F6E1}\u{FE0F}', '\u{26A1}', '\u{2705}', '\u{2757}', '\u{2764}\u{FE0F}'
];

export const GroupChatPane = ({
  activeRoom,
  channels,
  activeChannel,
  setActiveChannel,
  chatMessages,
  activeIdentity,
  chatInputText,
  setChatInputText,
  handleSendGroupMessage,
  handleUpdateMemberRole,
  handleLeaveRoom,
  setShowCreateChannelModal,
  triggerAlert,
  displayGaiaID,
  t,
  openContactProfile,
  handleOpenGroupSettings,
  handleDeleteChatMessage,
  handleClearGroupChannel,
  setActiveRoom,
  setMobileMenuOpen,
  messageMeta = {},
  onToggleMessagePin,
  onToggleMessageSaved,
  onReactToMessage,
  messageReplyTarget,
  setMessageReplyTarget
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  const [actionMessageId, setActionMessageId] = React.useState(null);
  const scrollRef = React.useRef(null);
  const longPressTimerRef = React.useRef(null);
  const isCrisis = activeRoom?.Description && activeRoom.Description.startsWith('[CRISIS]');

  const appendChatEmoji = emoji => {
    setChatInputText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const channelMessages = React.useMemo(() => {
    if (!activeChannel) return [];
    return chatMessages.filter(msg => msg.channelId === activeChannel.id);
  }, [activeChannel, chatMessages]);

  const lastMessageId = channelMessages.length > 0 ? channelMessages[channelMessages.length - 1].id : '';

  React.useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [activeRoom?.ID, activeChannel?.id, lastMessageId, channelMessages.length]);

  React.useEffect(() => () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
  }, []);

  const actorIsAdmin = !!activeRoom?.Members?.find(member => member.IdentityID === activeIdentity?.ID && member.Role === 'admin');
  const actorCanManage = !!(
    activeRoom &&
    activeIdentity &&
    (
      activeRoom.CreatorID === activeIdentity.ID ||
      activeRoom.CreatedBy === activeIdentity.ID ||
      actorIsAdmin
    )
  );

  const isOwnMessage = message => (
    (message.sender && activeIdentity?.ID && message.sender === activeIdentity.ID) ||
    (message.sender && activeIdentity?.GaiaID && parseToGaiaID(message.sender) === parseToGaiaID(activeIdentity.GaiaID)) ||
    (message.senderGaia && activeIdentity?.GaiaID && parseToGaiaID(message.senderGaia) === parseToGaiaID(activeIdentity.GaiaID))
  );

  const senderNameFor = message => {
    if (isOwnMessage(message)) {
      return activeIdentity?.DisplayName || activeIdentity?.displayName || t('you') || 'Du';
    }
    const senderMember = activeRoom?.Members?.find(member =>
      member.IdentityID === message.sender ||
      (member.Identity?.GaiaID && parseToGaiaID(member.Identity.GaiaID) === parseToGaiaID(message.sender)) ||
      (member.Identity?.GaiaID && parseToGaiaID(member.Identity.GaiaID) === parseToGaiaID(message.senderGaia))
    );
    return senderMember?.Identity?.DisplayName ||
      senderMember?.Username ||
      (message.senderGaia ? displayGaiaID(message.senderGaia) : '') ||
      (message.sender ? displayGaiaID(message.sender) : t('unknown_sender') || 'Unbekannt');
  };

  const openMessageActions = (event, messageId) => {
    event.preventDefault();
    event.stopPropagation();
    setActionMessageId(messageId);
  };

  const startLongPress = messageId => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
    longPressTimerRef.current = window.setTimeout(() => {
      setActionMessageId(messageId);
    }, 520);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const jumpToMessage = messageId => {
    if (!scrollRef.current) return;
    const target = scrollRef.current.querySelector(`[data-message-id="${messageId}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const replyToMessage = message => {
    setMessageReplyTarget({
      messageId: message.id,
      sender: message.sender,
      senderGaia: message.senderGaia,
      bodyPreview: messagePreview(message),
      createdAt: message.createdAt,
      channelId: message.channelId,
      roomId: message.roomId
    });
  };

  if (!activeRoom) {
    return (
      <aside className="group-sidebar empty-group-state">
        {t('keine_gruppe_ausgewaehlt') || 'Keine Gruppe ausgewahlt.'}
      </aside>
    );
  }

  return (
    <div className="group-chat-layout">
      <div className="detail-mobile-actions">
        <button type="button" className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)}>
          {t('menu') || 'Menu'}
        </button>
        <button type="button" className="mobile-back-btn" onClick={() => setActiveRoom(null)}>
          {t('gruppen_chats') || 'Gruppen'}
        </button>
        {activeChannel && (
          <button type="button" className="mobile-back-btn" onClick={() => setActiveChannel(null)}>
            {t('kanaele') || 'Kanaele'}
          </button>
        )}
      </div>

      <aside className="group-sidebar">
        <div className="group-nav-row">
          <div className="group-room-title">{activeRoom.Name}</div>
          <div className="group-nav-actions">
            {actorCanManage && (
              <button className="btn-action group-compact-action" onClick={() => setShowCreateChannelModal(true)}>
                {t('kanal_erstellen') || '+ Kanal'}
              </button>
            )}
            {actorCanManage && (
              <button
                type="button"
                className="btn-secondary group-settings-icon"
                onClick={handleOpenGroupSettings}
                title={t('group_settings_title') || 'Einstellungen'}
              >
                {'\u2699'}
              </button>
            )}
          </div>
        </div>

        <div className="channel-list">
          <div className="channel-header">
            <span>{t('kanaele') || '# Kanaele'}</span>
          </div>
          {channels.map(channel => (
            <button
              type="button"
              key={channel.id}
              className={`channel-item ${activeChannel?.id === channel.id ? 'active' : ''}`}
              onClick={() => setActiveChannel(channel)}
            >
              <span># {channel.name}</span>
            </button>
          ))}
        </div>

        <div className="group-sidebar-title">
          {t('mitglieder') || 'Mitglieder'} ({activeRoom.Members?.length || 0})
        </div>
        <div className="group-members-list gaia-scrollbar">
          {activeRoom.Members?.map(member => {
            const isSelf = member.IdentityID === activeIdentity?.ID;
            const isAdmin = member.Role === 'admin';
            return (
              <button
                type="button"
                key={member.IdentityID}
                className="group-member-item"
                onClick={() => member.Identity?.GaiaID && openContactProfile(member.Identity.GaiaID)}
              >
                <span className="group-member-copy">
                  <strong>{member.Identity?.DisplayName || member.Username} {isSelf && `(${t('you') || 'Du'})`}</strong>
                  <small>{member.Identity ? displayGaiaID(member.Identity.GaiaID) : ''}</small>
                </span>
                <span onClick={event => event.stopPropagation()}>
                  {actorIsAdmin && !isSelf ? (
                    <select
                      className="member-role"
                      value={member.Role}
                      onChange={event => handleUpdateMemberRole(member.IdentityID, event.target.value)}
                    >
                      <option value="member">{t('mitglied') || 'Mitglied'}</option>
                      <option value="admin">{t('admin') || 'Admin'}</option>
                    </select>
                  ) : (
                    <span className={`member-role ${isAdmin ? 'admin' : ''}`}>
                      {isAdmin ? (t('admin') || 'admin') : (t('mitglied') || 'member')}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {isCrisis ? (
          <>
            <div className="group-policy-box">
              <div className="group-policy-title">{t('room_policy_title') || 'Raum-Richtlinien'}</div>
              <div>
                <div><strong>{t('room_policy_crisis') || 'Krisenraum'}:</strong> {t('room_policy_audit') || 'Audit-Trail und Key-Rotation aktiv.'}</div>
                <div><strong>{t('room_policy_zero_knowledge') || 'Policy'}:</strong> {t('room_policy_e2ee') || 'Zero-Knowledge-Raum. E2EE quantensicher.'}</div>
              </div>
            </div>
            <div className="group-policy-box pinned-security-note">
              <div className="group-policy-title">{t('room_security_note_title') || 'Angeheftete Sicherheitsnotiz'}</div>
              <div>
                {t('room_security_note_desc') || 'Alle Schlusselwechsel werden uber Key Transparency validiert. Nutzen Sie GaiaVault fur private Backup-Logs.'}
              </div>
            </div>
          </>
        ) : (
          <div className="group-policy-box muted">
            <div className="group-policy-title">{t('status') || 'Status'}</div>
            <div>{t('standard_group_status') || 'Standard-Gruppenchat (E2EE verschlusselt)'}</div>
          </div>
        )}

        <div className="group-leave-box">
          <button className="btn-secondary group-leave-btn" onClick={() => handleLeaveRoom(activeRoom.ID)}>
            {t('gruppe_verlassen') || 'Gruppe verlassen'}
          </button>
          {activeRoom.SecretHash && (
            <div className="group-invite-box">
              <span>{t('einladungscode') || 'Einladungscode:'}</span>
              <code>{activeRoom.SecretHash}</code>
              <button
                className="btn-action"
                onClick={() => {
                  navigator.clipboard.writeText(activeRoom.SecretHash);
                  triggerAlert(t('kopiert') || 'Kopiert', t('code_kopieren_success') || 'Der Einladungscode wurde kopiert.');
                }}
              >
                {t('code_kopieren') || 'Code kopieren'}
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="chat-container group-main-chat">
        {activeChannel ? (
          <>
            <header className="reader-header chat-reader-header">
              <div className="chat-peer-header">
                <div className="chat-peer-icon" aria-hidden="true">#</div>
                <div>
                  <h3>#{activeChannel.name}</h3>
                  <span>{t('channel_id') || 'Kanal-ID'}: {activeChannel.id}</span>
                </div>
              </div>
              <button type="button" className="btn-secondary chat-header-action" onClick={handleClearGroupChannel}>
                {t('clear_chat') || 'Clear Chat'}
              </button>
            </header>

            <PinnedMessagesStrip
              messages={channelMessages}
              messageMeta={messageMeta}
              onJumpToMessage={jumpToMessage}
              t={t}
            />

            <div className="chat-messages gaia-scrollbar" ref={scrollRef} onClick={() => setActionMessageId(null)}>
              {channelMessages.map(message => {
                const outgoing = isOwnMessage(message);
                const meta = messageMeta[message.id] || {};
                return (
                  <div
                    key={message.id}
                    data-message-id={message.id}
                    className={`chat-bubble ${outgoing ? 'outgoing' : 'incoming'} ${meta.pinned ? 'pinned' : ''} ${meta.saved ? 'saved' : ''}`}
                    onContextMenu={event => openMessageActions(event, message.id)}
                    onTouchStart={() => startLongPress(message.id)}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                  >
                    {!outgoing && (
                      <button
                        type="button"
                        className="link-button contact-name-button group-sender-button"
                        onClick={() => openContactProfile(message.senderGaia || message.sender)}
                      >
                        {senderNameFor(message)}
                      </button>
                    )}
                    <ReplyContext replyTo={message.replyTo} t={t} />
                    <div>{renderMarkdown(message.body)}</div>
                    <MessageReactionStrip meta={meta} />
                    <div className="chat-bubble-meta">
                      <span>{new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      {meta.saved && <span>{t('message_saved') || 'Saved'}</span>}
                      <button type="button" className="chat-delete-btn" onClick={() => handleDeleteChatMessage(message.id)}>
                        {t('delete') || 'Delete'}
                      </button>
                      {message.untrusted && <span className="chat-untrusted">{t('untrusted') || 'Untrusted'}</span>}
                    </div>
                    <MessageActionMenu
                      open={actionMessageId === message.id}
                      message={message}
                      meta={meta}
                      onClose={() => setActionMessageId(null)}
                      onTogglePin={onToggleMessagePin}
                      onToggleSave={onToggleMessageSaved}
                      onReply={replyToMessage}
                      onReact={onReactToMessage}
                      t={t}
                    />
                  </div>
                );
              })}
              {channelMessages.length === 0 && (
                <div className="chat-empty-hint">
                  {t('room_chat_start_hint') || 'Starten Sie die Konversation! Alle Nachrichten in diesem Gruppenkanal sind dezentral quantensicher E2E verschlusselt.'}
                </div>
              )}
            </div>

            <div className="chat-composer-stack">
              <ReplyComposerPreview
                replyTarget={messageReplyTarget}
                onClear={() => setMessageReplyTarget(null)}
                t={t}
              />
              <form className="chat-input-row" onSubmit={handleSendGroupMessage}>
                <div className="emoji-control">
                  <button type="button" className="btn-secondary emoji-toggle" onClick={() => setShowEmojiPicker(prev => !prev)}>
                    {'\u{1F642}'}
                  </button>
                  {showEmojiPicker && (
                    <div className="emoji-picker" role="listbox" aria-label="Emoji Auswahl">
                      {GROUP_EMOJIS.map(emoji => (
                        <button type="button" key={emoji} onClick={() => appendChatEmoji(emoji)}>
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  className="input-field"
                  placeholder={`${t('message_to_channel') || 'Nachricht an'} #${activeChannel.name}`}
                  value={chatInputText}
                  onChange={event => setChatInputText(event.target.value)}
                  required
                />
                <button type="submit" className="btn-primary">
                  {t('senden') || 'Senden'}
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="empty-chat-state">
            <h3>{t('kein_kanal_ausgewaehlt') || 'Kein Kanal ausgewahlt'}</h3>
            <p>{t('select_channel_chat') || 'Wahle einen Kanal aus der linken Seitenleiste aus, um zu chatten.'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupChatPane;
